<template>
  <div>
    <div class="mc-route-optimize-counter__quota-item">
      <div class="mc-route-optimize-counter__header">
        <div class="d-flex">
          <p class="mb-0">{{ $t('routeOptimization.fields.quotaOptimization') }}</p>
          <McInfoTooltip
            id="quota"
            class="mc-route-optimize-counter__info-tooltip"
            :tooltip="tooltip"
          />
        </div>
        <div>
          <span class="mc-route-optimize-counter__value" :style="{ color: quotaColor }">
            {{ formatNumber(quota.usage) }}
          </span>
          <span class="mc-route-optimize-counter__total">
            / {{ isUnlimited ? '∞' : formatNumber(quota.limit) }}
          </span>
        </div>
      </div>

      <div
        ref="progressWrapper"
        class="mc-route-optimize-counter__progress-wrapper"
        @mousemove="handleMouseMove"
        @mouseleave="showTooltip = false"
      >
        <div class="mc-route-optimize-counter__progress-container">
          <div
            class="mc-route-optimize-counter__progress-fill"
            :style="{ width: progressWidth, backgroundColor: quotaColor }"
          ></div>
        </div>
      </div>

      <div
        v-if="showTooltip"
        class="mc-route-optimize-counter__tooltip"
        :style="{ top: tooltipY + 'px', left: tooltipX + 'px' }"
      >
        {{ tooltipText }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import McInfoTooltip from '@/components/McInfoTooltip'
import * as RO from '@/services/rest-api/lib/route-optimization'

export default {
  components: { McInfoTooltip },
  props: {
    refreshTrigger: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      quota: { usage: 0, limit: 0, remaining: 0, period: '' },
      showTooltip: false,
      tooltipText: '',
      tooltipX: 0,
      tooltipY: 0,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isUnlimited() {
      return !this.quota.period || this.quota.period === 'unlimited'
    },
    tooltip() {
      return this.isUnlimited
        ? this.$t('routeOptimization.tooltip.unlimitedQuotaInfo')
        : this.$t('routeOptimization.tooltip.description')
    },
    quotaColor() {
      if (this.isUnlimited) return '#429677'
      const ratio = this.quota.limit ? this.quota.usage / this.quota.limit : 0
      if (ratio < 0.8) {
        return '#429677'
      }
      if (ratio < 1) {
        return '#FFA133'
      }
      return '#F64A33'
    },
    progressWidth() {
      if (this.isUnlimited) return '100%'
      if (!this.quota.limit) return '0%'
      const percentage = (this.quota.usage / this.quota.limit) * 100
      return Math.min(percentage, 100) + '%'
    },
    quotaStatus() {
      const ratio = this.quota.limit ? this.quota.usage / this.quota.limit : 0
      if (ratio < 0.8) {
        return 'available'
      }
      if (ratio < 1) {
        return 'low'
      }
      return 'empty'
    },
  },
  watch: {
    refreshTrigger() {
      this.fetchQuota()
    },
  },
  mounted() {
    this.fetchQuota()
  },
  methods: {
    async fetchQuota() {
      const { data } = await RO.getRouteOptimizationUsage()
      if (data) {
        const usage = data.usage ?? 0
        const limit = data.limit ?? 0
        this.quota = {
          usage,
          limit,
          remaining: data.remaining ?? Math.max(limit - usage, 0),
          period: data.period || '',
        }
      }

      this.$emit('quotaStatus', this.quotaStatus)
    },
    formatNumber(num, format = 'short', separator = '.') {
      if (num >= 1000000000 && format === 'short') {
        return (num / 1000000000).toFixed(2).replace(/\.00$/, '') + 'M'
      }
      if (num >= 1000000 && format === 'short') {
        return (num / 1000000).toFixed(2).replace(/\.00$/, '') + 'B'
      }
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator)
    },
    handleMouseMove(event) {
      const wrapper = this.$refs.progressWrapper
      const rect = wrapper.getBoundingClientRect()
      const offsetX = event.clientX - rect.left
      const usageWidth = this.quota.limit
        ? Math.min((this.quota.usage / this.quota.limit) * rect.width, rect.width)
        : 0

      if (offsetX <= usageWidth || this.isUnlimited) {
        this.tooltipText = `${this.$t('routeOptimization.fields.used')}: ${this.formatNumber(
          this.quota.usage,
          'long',
          ','
        )}`
      } else if (!this.isUnlimited) {
        this.tooltipText = `${this.$t('routeOptimization.fields.remaining')}: ${this.formatNumber(
          this.quota.remaining,
          'long',
          ','
        )}`
      } else {
        this.showTooltip = false
        return
      }

      const tooltipWidth = 120
      this.tooltipX = Math.min(
        Math.max(event.clientX - tooltipWidth / 2, 0),
        window.innerWidth - tooltipWidth
      )
      this.tooltipY = rect.bottom + 6
      this.showTooltip = true
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-route-optimize-counter {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.mc-route-optimize-counter__quota-item {
  width: 300px;
}

.mc-route-optimize-counter__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  margin-bottom: 6px;
}

.mc-route-optimize-counter__value {
  font-weight: bold;
}

.mc-route-optimize-counter__info-tooltip {
  margin-left: 5px;
}

.mc-route-optimize-counter__total {
  margin-left: 2px;
}

.mc-route-optimize-counter__progress-wrapper {
  position: relative;
  width: 100%;
}

.mc-route-optimize-counter__progress-container {
  height: 12px;
  background-color: $teal-100;
  border-radius: 8px;
}

.mc-route-optimize-counter__progress-fill {
  height: 100%;
  border-radius: 8px;
  transition: width 0.3s ease;
}

.mc-route-optimize-counter__tooltip {
  position: fixed;
  background: $teal-400;
  color: $white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 9999;
}
</style>
